import React, { useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  Legend,
  CartesianGrid,
} from "recharts";

interface MonthlyCostChartProps {
  sopCostData: any;
}

export const MonthlyCostChart: React.FC<MonthlyCostChartProps> = ({
  sopCostData,
}) => {
  const [timePeriod, setTimePeriod] = useState("30");

  const costData = sopCostData?.result?.monthlyTrend?.map((item) => ({
    month: item.month,
    expected: item.expectedCost,
    actual: item.actualCost,
    jobCount: item.jobCount,
  }));

  const currentMonth = sopCostData?.result?.currentMonthData;
  const currentMonthName =
    sopCostData?.result?.monthlyTrend?.slice(-1)[0]?.month;

  const expectedCost = currentMonth?.expectedCost;
  const actualCost = currentMonth?.actualCost;
  const variancePercentage = currentMonth?.variancePercentage;
  const varianceChange = currentMonth?.varianceChange;

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-2 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-900 text-xs">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ color: entry.color }} className="text-xs">
              {entry.name}: ${entry.value}K
            </p>
          ))}
          {data.jobCount > 0 && (
            <p className="text-xs text-gray-600">Jobs: {data.jobCount}</p>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      <div className="h-1 " />
      <div className="p-2">
        <div className="mb-2 flex justify-between items-start">
          <div>
            <h2 className="text-sm font-bold text-gray-900">
              Monthly Service Order Cost
            </h2>
            <p className="text-xs text-gray-600 italic">
              Track expected vs actual service costs
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-1.5 mb-3">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 text-center">
            <div className="text-xs text-gray-500 mb-0.5">
              Expected ({currentMonthName})
            </div>
            <div className="text-lg font-bold text-blue-600">
              {expectedCost >= 1000 ? `${expectedCost}` : expectedCost}
            </div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-2 text-center">
            <div className="text-xs text-gray-500 mb-0.5">
              Actual ({currentMonthName})
            </div>
            <div className="text-lg font-bold text-green-600">
              {actualCost >= 1000 ? `${actualCost}` : actualCost}
            </div>
            <div
              className={`text-xs font-semibold ${
                variancePercentage >= 0 ? "text-red-600" : "text-green-600"
              }`}
            >
              {variancePercentage >= 0 ? "+" : ""}
              {variancePercentage}%
            </div>
          </div>
        </div>
        <div className="h-40">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={costData}
              margin={{ top: 5, right: 15, left: 5, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="month"
                axisLine={true}
                tickLine={true}
                tick={{ fontSize: 10, fill: "#6b7280" }}
                stroke="#e5e7eb"
              />
              <YAxis
                axisLine={true}
                tickLine={true}
                tick={{ fontSize: 10, fill: "#6b7280" }}
                tickFormatter={(value) => `$${value}K`}
                stroke="#e5e7eb"
                label={{
                  value: "Cost ($K)",
                  angle: -90,
                  position: "insideLeft",
                  style: { fontSize: "10px" },
                }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: "10px" }} />
              <Line
                type="monotone"
                dataKey="expected"
                stroke="#3b82f6"
                strokeWidth={2}
                strokeDasharray="5 5"
                name="Expected"
                dot={{ fill: "#3b82f6", strokeWidth: 2, r: 3 }}
              />
              <Line
                type="monotone"
                dataKey="actual"
                stroke="#10b981"
                strokeWidth={2}
                name="Actual"
                dot={{ fill: "#10b981", strokeWidth: 2, r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};
